/**
 * FocusTrap - lightweight, hand-rolled focus trap for modal surfaces.
 *
 * `focus-trap-react` is intentionally NOT a dependency of this prototype
 * (per the PROD-571 brief: hand-roll a small util rather than pull in a
 * third-party modal lib). The trap is the minimum needed to satisfy HC6
 * a11y for `OverrideModal`:
 *
 *   1. On open: capture the previously-focused element so the modal can
 *      restore focus when it closes.
 *   2. Move focus to the first interactive element inside the trap
 *      (auto-focused first field per HC6).
 *   3. Trap Tab + Shift+Tab inside the focusable set so keyboard users
 *      cannot accidentally tab into the page background while the modal
 *      is open.
 *   4. ESC closes the trap (caller decides what "close" means by
 *      providing `onEscape`).
 *
 * The trap does NOT use `aria-hidden="true"` on the page background.
 * That pattern can create a "trap" for screen readers when the modal
 * itself is not properly focused; we rely on `role="dialog"` +
 * `aria-modal="true"` on the modal shell + the focus trap to keep SR
 * announcement scoped to the dialog.
 *
 * No timing, no animation - the trap is a passive `useEffect` hook.
 * Determinism: focus moves are synchronous on mount; the only delayed
 * call is a `requestAnimationFrame` for the initial focus (so the
 * dialog has rendered before we focus into it). Tests poll for the
 * focused element so the rAF is observable.
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export interface UseFocusTrapOptions {
  /** Whether the trap is active. When false, the hook is a no-op. */
  enabled: boolean;
  /** Called when the user presses Escape inside the trap. */
  onEscape: () => void;
}

/**
 * Attach a focus trap to `containerRef`. Returns nothing; effects own
 * the lifecycle. Caller must render the modal shell with the ref set.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  options: UseFocusTrapOptions,
): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const { enabled, onEscape } = options;

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    // Capture the element that triggered the modal so we can return
    // focus on close (HC6: focus returns to trigger).
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusableElements = (): HTMLElement[] => {
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    };

    // Move focus to the first interactive element. rAF so the dialog
    // is mounted before the focus call lands. If focus has already
    // moved INSIDE the container (e.g. the user clicked a field
    // immediately after open), respect that and do not yank it away.
    const focusFrame = requestAnimationFrame(() => {
      if (
        document.activeElement instanceof HTMLElement &&
        container.contains(document.activeElement) &&
        document.activeElement !== container
      ) {
        return;
      }
      const elements = focusableElements();
      if (elements.length > 0) {
        elements[0].focus();
      } else {
        container.focus();
      }
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusableElements();
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !container.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const previous = previousFocusRef.current;

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the trigger that opened the modal. Guard
      // against the trigger being unmounted between open + close.
      if (previous && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [containerRef, enabled, onEscape]);
}
